/**
 * Flying into a rock.
 *
 * ── Why this is not just "test every asteroid" ──────────────────────────────
 *
 * A large belt is nine hundred rocks and a system holds several fields. Testing
 * all of them every frame is thousands of tests at sixty frames a second, which
 * is the same shape of mistake as the collision freeze: work that looks like it
 * happens once, happening per element.
 *
 * So it is three tests, cheapest first, and each one throws away most of what
 * the next would have looked at.
 *
 *   1. Is the ship in the band at all? One test per *field* — distance from the
 *      ring's centre line, and height off its plane. Almost always false, and it
 *      discards nine hundred rocks in a handful of operations.
 *
 *   2. Which arc? Rocks are generated in even angular slots, so a rock's index
 *      *is* roughly its angle: `index / count` of the way round. The ship's own
 *      angle therefore names the slot to look in without any structure being
 *      built or kept in step — the ordering already exists in the generator.
 *
 *   3. Sphere against sphere, on the few rocks that survived.
 *
 * That is one cheap test per field and then a dozen rocks, whatever the belt's
 * size.
 *
 * ── Everything here is in the field's own frame ─────────────────────────────
 *
 * Not the system's, and not the scene's. The field frame rotates as the belt
 * orbits, so a caller converts the ship into it once and everything below is
 * static — which is what makes the arithmetic simple enough to be obviously
 * right. Getting that conversion wrong is silent, so it is the caller's one job.
 *
 * ── And the ring is not the xy plane ────────────────────────────────────────
 *
 * Even inside that frame, a field's orbit is inclined — so "distance from the
 * centre line" is not `hypot(x, y)` and "height off the plane" is not `z`. The
 * first version of this assumed both and found nothing, ever, because it was
 * measuring a tilted ring against a flat one.
 *
 * So the caller passes the ring's own basis: `along` points at the rock the
 * generator calls anomaly zero, `across` at a quarter turn on, and `normal`
 * stands off the plane. Those three turn every question below into a dot
 * product — and the renderer already computes them to spin the belt, so nothing
 * new is derived and there is no second opinion about where the plane is.
 */
/**
 * How many slots either side of the ship's own to check.
 *
 * One is enough while a ship is smaller than the gap between rocks, which it is
 * — but a slot is an *approximate* home for a rock, since each one is jittered
 * within its slot and rides its own orbit. Two makes the search a handful of
 * rocks instead of three and removes the class of bug where something passes
 * between two tests because it was filed next door.
 */
const SLOT_REACH = 2;
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
/**
 * Is the ship anywhere near this band?
 *
 * The one test that runs for every field, every frame. Everything else is
 * behind it.
 */
export function nearField(field, shipInField, shipRadius, basis) {
    /*
     * How far a rock can sit from the centre line.
     *
     * The band's own width, plus what eccentricity moves the ring by: an orbit
     * with any eccentricity is closer to the focus at one end than the other, by
     * roughly `a * e`. At a few thousand units even a hundredth of that is tens of
     * units — comfortably more than a narrow belt is wide — so leaving it out
     * rejected rocks the ship was sitting on.
     */
    const room = field.radius +
        shipRadius +
        field.orbit.semiMajorAxis * Math.abs(field.orbit.eccentricity);
    // How far off the plane, measured along its normal rather than up the frame's
    // z — the ring is tilted, and a belt is thin enough that this is the test that
    // rejects most of the sky.
    const height = dot(shipInField, basis.normal);
    if (Math.abs(height) > field.radius * 0.5 + shipRadius)
        return false;
    // And how far from the centre line, in the plane itself.
    const reach = Math.hypot(dot(shipInField, basis.along), dot(shipInField, basis.across));
    return Math.abs(reach - field.orbit.semiMajorAxis) <= room;
}
/**
 * The rock the ship is inside, if it is inside one.
 *
 * `positions` holds each rock's centre in the field's frame, three floats per
 * rock, in the same order as `rocks` — the caller has already computed these to
 * place the instances, so this reads them rather than working them out again.
 *
 * Returns the *deepest* overlap rather than the first found. A ship clipping the
 * corner of one rock while buried in another should be pushed out of the one
 * that has hold of it.
 */
export function asteroidHit(field, rocks, positions, shipInField, shipRadius, basis, broken) {
    if (rocks.length === 0 || !nearField(field, shipInField, shipRadius, basis))
        return null;
    /*
     * Which slot the ship is in.
     *
     * Measured in the ring's own plane, against the axis the generator counts
     * anomalies from — so this lands on the same numbering `asteroidsIn` used.
     * `atan2` gives −π..π and slots are laid out from zero, so it is shifted into
     * 0..2π before being divided. Getting either wrong puts the search half a belt
     * from the ship, and it finds nothing, always.
     */
    const angle = Math.atan2(dot(shipInField, basis.across), dot(shipInField, basis.along));
    const turn = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const home = Math.floor((turn / (Math.PI * 2)) * rocks.length);
    let best = null;
    for (let step = -SLOT_REACH; step <= SLOT_REACH; step++) {
        // Wrapped, because a belt closes. A ship at the seam is beside the rocks at
        // both ends of the list.
        const index = ((home + step) % rocks.length + rocks.length) % rocks.length;
        if (broken?.has(index))
            continue;
        const rock = rocks[index];
        const dx = shipInField.x - positions[index * 3];
        const dy = shipInField.y - positions[index * 3 + 1];
        const dz = shipInField.z - positions[index * 3 + 2];
        const gap = Math.hypot(dx, dy, dz);
        const touching = rock.radius + shipRadius;
        if (gap >= touching)
            continue;
        const depth = touching - gap;
        if (best && depth <= best.depth)
            continue;
        /*
         * Straight up, if the ship is exactly on the centre.
         *
         * Only reachable by flying into a rock's dead centre, which is a measure
         * zero event that nonetheless happens — and a zero-length normal turns the
         * whole response into NaN, which spreads through the scene graph.
         */
        const normal = gap > 1e-6
            ? { x: dx / gap, y: dy / gap, z: dz / gap }
            : { x: 0, y: 0, z: 1 };
        best = { index, normal, depth, rock };
    }
    return best;
}
//# sourceMappingURL=asteroidHit.js.map